import React, { useState, useMemo, useEffect, useContext } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/modern/components/ui/tabs";
import { Button } from "@/modern/components/ui/button";
import { Upload, Play, X, Loader2, Copy } from "lucide-react";
import { HTMLRenderer } from "@/modern/components/ui/html-renderer";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import BryntumContext from "@/react/context/bryntumContext";
import { getDraftContextPrompt, getDraftFieldAdvisorPrompt, uploadFile, generateAIInsight } from "@/network/network";
import { copyTextToClipboard } from "@/other/utils/utils";
import { toast } from "sonner";
import { InspectionType, AdvisorType } from "../constants/settingsStructure";

interface TestPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  editorInstance: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  inspectionType?: InspectionType;
  advisorType?: AdvisorType;
}

const TestPromptModal = ({ isOpen, onClose, editorInstance, inspectionType, advisorType }: TestPromptModalProps) => {
  const { selectedBoard } = useContext(BryntumContext);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [testResults, setTestResults] = useState<string[]>([]);
  const [compiledPrompt, setCompiledPrompt] = useState<string>("");
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [isImageCollapsed, setIsImageCollapsed] = useState(false);

  // Create a dummy record for BaseModal
  const dummyRecord = useMemo(() => ({ isPhantom: true, archived: false }), []);

  // Fetch compiled prompt when modal opens
  useEffect(() => {
    const fetchPrompt = async () => {
      // @ts-expect-error - selectedBoard.record type not fully defined
      if (!isOpen || !selectedBoard?.record?.id) return;

      setIsLoadingPrompt(true);
      try {
        // @ts-expect-error - selectedBoard.record type not fully defined
        const boardId = selectedBoard.record.id;

        let response;
        if (advisorType) {
          response = await getDraftFieldAdvisorPrompt(boardId, advisorType);
          setCompiledPrompt(response?.data?.draft_fieldadvisor_prompt || "");
        } else {
          response = await getDraftContextPrompt(boardId, inspectionType || "site_safety");
          setCompiledPrompt(response?.data?.draft_context_prompt || "");
        }
      } catch (error) {
        console.error("Error fetching compiled prompt:", error);
        setCompiledPrompt("Failed to load compiled prompt. Please try again.");
      } finally {
        setIsLoadingPrompt(false);
      }
    };

    fetchPrompt();
  }, [isOpen, selectedBoard, inspectionType, advisorType]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedImage(null);
      setImageFile(null);
      setIsRunning(false);
      setTestResults([]);
      setCompiledPrompt("");
      setIsImageCollapsed(false);
    }
  }, [isOpen]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setTestResults([]); // Clear previous results
      setIsImageCollapsed(false); // Reset to expanded view
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setTestResults([]);
      setIsImageCollapsed(false); // Reset to expanded view
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImageFile(null);
    setTestResults([]);
    setIsImageCollapsed(false);
  };

  const handleRunTest = async () => {
    if (!imageFile) return;

    // @ts-expect-error - selectedBoard.record type not fully defined
    if (!selectedBoard?.record?.id) {
      toast.error("Board not found");
      return;
    }

    setIsRunning(true);
    setTestResults([]);

    try {
      // Step 1: Upload the file
      const response = await uploadFile(imageFile, imageFile.name);
      const { id: documentId } = response.data;

      if (!documentId) {
        throw new Error("Failed to upload file");
      }

      // Step 2: Call the AI insight generation API with insight_type
      const result = await generateAIInsight({
        document_id: documentId,
        // @ts-expect-error - selectedBoard.record type not fully defined
        board_id: selectedBoard.record.id,
        insight_type: advisorType || inspectionType || "site_safety",
        language: "en"
      });

      // Set the AI-generated insights as an array
      const insights = result.data.insights || [];
      setTestResults(insights.length > 0 ? insights : ["No insights generated"]);

      // Collapse image to show results
      setIsImageCollapsed(true);

      toast.success("AI inspection completed successfully");
    } catch (error) {
      console.error("Error generating AI inspection:", error);
      setTestResults(["Failed to generate AI inspection. Please try again."]);
      toast.error("Failed to generate AI inspection");
    } finally {
      setIsRunning(false);
    }
  };

  const handleCopyPrompt = () => {
    if (!compiledPrompt) {
      toast.error("No prompt to copy");
      return;
    }
    copyTextToClipboard(compiledPrompt, "Prompt copied to clipboard");
  };

  const handleCopyResults = () => {
    if (!testResults || testResults.length === 0) {
      toast.error("No results to copy");
      return;
    }
    copyTextToClipboard(testResults.join("\n\n"), "Results copied to clipboard");
  };

  const MODAL_SIZE_CONFIG = {
    width: "95vw",
    height: "90vh",
    minWidth: "95vw",
    minHeight: "90vh",
    maxWidth: "95vw",
    maxHeight: "90vh"
  };

  if (!isOpen) return null;

  return (
    <BaseModal
      title="AI Test Prompt"
      description=""
      onClose={onClose}
      size={MODAL_SIZE_CONFIG}
      editorInstance={editorInstance}
      record={dummyRecord}
      className="resize-none"
      headerIconCls="fa-regular fa-flask-vial"
      modal={true}
      version=""
      hideMenu={true}
      footerRenderer={null}
      swivuAssistanceConfig={undefined}
    >
      <BaseModalBody className="!p-0 !overflow-hidden flex flex-col h-full">
        {/* Desktop: Split View, Mobile: Tabs */}
        <div className="flex-1 h-full overflow-hidden">
          {/* Desktop View (>= 1024px) */}
          <div className="hidden lg:flex h-full">
            {/* Left Panel - Compiled Prompt */}
            <div className="w-2/5 border-r border-border overflow-y-auto bg-muted/30 flex-shrink-0">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Compiled Prompt</h3>
                {isLoadingPrompt ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="space-y-4 text-sm">
                    <div className="relative p-4 bg-card rounded-lg border border-border">
                      <Button variant="ghost" size="icon-sm" onClick={handleCopyPrompt} disabled={!compiledPrompt} className="absolute top-2 right-2">
                        <Copy className="h-4 w-4" />
                      </Button>
                      {compiledPrompt ? (
                        <pre className="text-muted-foreground whitespace-pre-wrap font-sans pr-3">{compiledPrompt}</pre>
                      ) : (
                        <p className="text-muted-foreground pr-3">No compiled prompt available. Please configure your context cards in the AI Prompt settings.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Test Playground */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <TestPlayground
                selectedImage={selectedImage}
                isRunning={isRunning}
                testResults={testResults}
                onImageUpload={handleImageUpload}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onRemoveImage={handleRemoveImage}
                onRunTest={handleRunTest}
                isImageCollapsed={isImageCollapsed}
                onToggleCollapse={() => setIsImageCollapsed(!isImageCollapsed)}
                onCopyResults={handleCopyResults}
              />
            </div>
          </div>

          {/* Mobile/Tablet View (< 1024px) */}
          <div className="lg:hidden h-full flex flex-col overflow-hidden">
            <Tabs defaultValue="test" className="h-full flex flex-col min-h-0">
              <div className="px-6 pt-6 flex-shrink-0">
                <TabsList className="w-full mb-4">
                  <TabsTrigger value="compiled" className="flex-1">
                    Compiled Prompt
                  </TabsTrigger>
                  <TabsTrigger value="test" className="flex-1">
                    Test Prompt
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="compiled" className="flex-1 overflow-y-auto mt-0">
                <div className="px-6 pb-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Compiled Prompt</h3>
                  {isLoadingPrompt ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="space-y-4 text-sm">
                      <div className="relative p-4 bg-card rounded-lg border border-border">
                        <Button variant="ghost" size="icon-sm" onClick={handleCopyPrompt} disabled={!compiledPrompt} className="absolute top-2 right-2">
                          <Copy className="h-4 w-4" />
                        </Button>
                        {compiledPrompt ? (
                          <pre className="text-muted-foreground whitespace-pre-wrap font-sans pr-3">{compiledPrompt}</pre>
                        ) : (
                          <p className="text-muted-foreground pr-3">No compiled prompt available. Please configure your context cards in the AI Prompt settings.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="test" className="flex-1 overflow-hidden mt-0 flex flex-col">
                <TestPlayground
                  selectedImage={selectedImage}
                  isRunning={isRunning}
                  testResults={testResults}
                  onImageUpload={handleImageUpload}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onRemoveImage={handleRemoveImage}
                  onRunTest={handleRunTest}
                  isImageCollapsed={isImageCollapsed}
                  onToggleCollapse={() => setIsImageCollapsed(!isImageCollapsed)}
                  onCopyResults={handleCopyResults}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};

interface TestPlaygroundProps {
  selectedImage: string | null;
  isRunning: boolean;
  testResults: string[];
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: React.DragEvent) => void;
  onDragOver: (event: React.DragEvent) => void;
  onRemoveImage: () => void;
  onRunTest: () => void;
  isImageCollapsed: boolean;
  onToggleCollapse: () => void;
  onCopyResults: () => void;
}

const TestPlayground = ({
  selectedImage,
  isRunning,
  testResults,
  onImageUpload,
  onDrop,
  onDragOver,
  onRemoveImage,
  onRunTest,
  isImageCollapsed,
  onToggleCollapse,
  onCopyResults
}: TestPlaygroundProps) => {
  return (
    <div className="p-6 space-y-6 flex-1 overflow-y-auto">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Test Prompt</h3>
        <p className="text-sm text-muted-foreground mb-4">Upload a construction photo to test the AI inspection prompt</p>

        {!selectedImage ? (
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer bg-muted/30"
          >
            <input type="file" id="image-upload" accept="image/*" onChange={onImageUpload} className="hidden" />
            <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Click to upload or drag and drop</p>
                <p className="text-xs text-muted-foreground">PNG, JPG, WEBP up to 10MB</p>
              </div>
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            {isImageCollapsed ? (
              // Collapsed View - Thumbnail
              <div className="flex items-center gap-4 p-4 border border-border rounded-lg bg-card">
                <div className="relative w-24 h-24 flex-shrink-0 rounded-md overflow-hidden border border-border">
                  <img src={selectedImage} alt="Selected construction" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground mb-1">Image uploaded</p>
                  <p className="text-xs text-muted-foreground">Click expand to view full size</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={onToggleCollapse}>
                    Expand
                  </Button>
                  <Button variant="ghost" size="icon-sm" onClick={onRemoveImage}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              // Expanded View - Full Image
              <div className="relative rounded-lg overflow-hidden border border-border bg-muted/30">
                <img src={selectedImage} alt="Selected construction" className="w-full h-auto max-h-[400px] object-contain" />
                <div className="absolute top-2 right-2 flex gap-2">
                  {testResults.length > 0 && (
                    <Button variant="secondary" size="sm" onClick={onToggleCollapse} className="bg-background/80 backdrop-blur-sm">
                      Collapse
                    </Button>
                  )}
                  <Button variant="destructive" size="icon-sm" onClick={onRemoveImage}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <Button onClick={onRunTest} disabled={isRunning} className="w-full" size="lg">
              {isRunning ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Running Test...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Run Test
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {testResults && testResults.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Test Results</h3>
            <Button variant="ghost" size="sm" onClick={onCopyResults}>
              <Copy className="h-4 w-4 mr-2" />
              Copy All
            </Button>
          </div>
          <div className="space-y-4">
            {testResults.map((insight, index) => (
              <div key={index} className="relative p-4 bg-card rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <HTMLRenderer content={insight} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestPromptModal;
